import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import CustomDropdown from '../components/CustomDropdown';
import { useModal } from '../contexts/ModalContext';

const ChecklistFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get ID from URL
  const { showAlert } = useModal();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Items State
  const [items, setItems] = useState([]); // Array of { type: 'check' | 'photo', content: '' }

  // NOTE: '사진첩 업로드 허용' / '최근 점검 내용 불러오기' / '점검자 이름 표시'
  // toggles used to live here. They were saved but never read anywhere, so the
  // switches did nothing. Removed rather than left as a false promise;
  // '최근 점검 내용 불러오기' is worth building properly later.

  useEffect(() => {
    if (id) {
      // Fetch existing checklist data
      setLoading(true);
      api.get(`/checklist-templates/${id}`)
        .then(res => {
          const data = res.data;
          setName(data.name);
          setDescription(data.description || ''); // Assuming description exists in model or added
          // Map items from backend format to frontend format
          const mappedItems = (data.items || []).map(item => {
            const typeMap = {
              '체크리스트': 'check',
              '체크박스': 'checkbox',
              '체크리스트(네/아니오)': 'check_yn',
              '텍스트 입력': 'input',
              '객관식 선택': 'choice',
              '섹션': 'SECTION'
            };
            const photoMap = {
              '없음': 'none',
              '선택': 'optional',
              '필수': 'required'
            };

            return {
              ...item,
              type: typeMap[item.responseType] || item.responseType || 'check',
              photoAttachmentOption: photoMap[item.photoAttachmentOption] || item.photoAttachmentOption || 'none'
            };
          });
          setItems(mappedItems);
        })
        .catch(err => {
          console.error("Failed to fetch checklist", err);
          showAlert("오류", "점검표 정보를 불러오는데 실패했습니다.", "danger");
          navigate('/', { state: { activeTab: 'settings' } });
        })
        .finally(() => setLoading(false));
    }
  }, [id, navigate, showAlert]);

  // Fetch templates for the modal
  const fetchTemplates = async () => {
    try {
      const res = await api.get('/checklist-templates');
      setAllTemplates(res.data);
      setIsModalOpen(true);
    } catch (err) {
      console.error("Failed to fetch templates", err);
      showAlert("오류", "점검표 목록을 불러오는데 실패했습니다.", "danger");
    }
  };

  const handleSelectTemplate = (template) => {
    setName(template.name);
    // If template has other fields, set them here
    // For now just name
    setIsModalOpen(false);
  };

  const handleAddItem = (index = items.length) => {
    const newItems = [...items];
    // Insert at index (default to end)
    // If index is event object (from click), use items.length
    const insertIndex = typeof index === 'number' ? index : items.length;
    newItems.splice(insertIndex, 0, {
      type: 'check',
      content: '',
      description: '',
      photoAttachmentOption: 'none',
      isRequired: false,
      options: ['옵션'] // Default option
    });
    setItems(newItems);
  };

  const handleOptionChange = (itemIndex, optionIndex, value) => {
    const newItems = [...items];
    newItems[itemIndex].options[optionIndex] = value;
    setItems(newItems);
  };

  const handleAddOption = (itemIndex) => {
    const newItems = [...items];
    if (!newItems[itemIndex].options) newItems[itemIndex].options = [];
    newItems[itemIndex].options.push(`옵션 ${newItems[itemIndex].options.length + 1}`);
    setItems(newItems);
  };

  const handleRemoveOption = (itemIndex, optionIndex) => {
    const newItems = [...items];
    newItems[itemIndex].options.splice(optionIndex, 1);
    setItems(newItems);
  };

  const handleAddSection = (index = items.length) => {
    const newItems = [...items];
    const insertIndex = typeof index === 'number' ? index : items.length;
    newItems.splice(insertIndex, 0, { type: 'SECTION', content: '', description: '' });
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showAlert("알림", "점검표 이름을 입력해주세요.", "warning");
      return;
    }

    if (items.length === 0) {
      showAlert("알림", "최소 1개 이상의 점검 항목을 등록해야 합니다.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name,
        description,
        items,
      };

      if (id) {
        await api.patch(`/checklist-templates/${id}`, payload);
        await showAlert("성공", "점검표가 수정되었습니다.", "success");
      } else {
        await api.post('/checklist-templates', payload);
        await showAlert("성공", "점검표가 등록되었습니다.", "success");
      }

      // Navigate back to Dashboard with 'settings' tab active
      navigate('/', { state: { activeTab: 'settings' } });
    } catch (err) {
      console.error("Failed to save checklist", err);
      showAlert("오류", "점검표 저장에 실패했습니다.", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checklist-form-page pb-5" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <style>{`
        .form-header {
          background-color: #fff;
          position: sticky;
          top: 0;
          z-index: 100;
          /* 위 여백은 .main-content 가 준다. 여기서 또 주면 다른 화면보다 벌어진다. */
          padding: 0 1rem 1rem;
          margin: 0 -1rem;
          display: flex;
          flex-direction: column; /* Stack vertically */
          gap: 1rem;
        }
        .header-top {
            display: flex;
            align-items: center;
            width: 100%;
        }
        .header-buttons {
            display: flex;
            gap: 0.5rem;
            width: 100%;
        }
        .back-btn {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          background-color: #fff;
          border: 1px solid #e2e8f0;
          color: #1e293b;
        }
        .page-title {
          font-size: 1.25rem;
          font-weight: 800;
          color: #1e293b;
          margin: 0;
        }
        .action-btn {
          padding: 0.75rem 1rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          flex: 1; /* Make buttons expand to fill width */
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .btn-load {
          background-color: #e0e7ff; /* Indigo 100 */
          color: #3730a3; /* Indigo 800 */
          border: none;
        }
        .btn-register {
          background-color: var(--primary-color); /* Primary */
          color: #fff;
          border: none;
        }
        .btn-register:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }
        .form-card {
          background: white;
          border-radius: 20px;
          padding: 1.25rem 1rem;
          box-shadow: 0 4px 20px rgba(0,0,0,0.03);
          border: 1px solid rgba(0,0,0,0.05);
        }
        .form-section {
          background-color: #fff;
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1rem;
        }
        .form-label {
          font-weight: 800;
          font-size: 1.1rem;
          margin-bottom: 1rem;
          color: #1e293b;
        }
        .form-control, .form-select {
          border-radius: 12px;
          padding: 0.75rem 1rem;
          border: 1px solid #e2e8f0;
          font-size: 1rem;
        }
        .form-control:focus, .form-select:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .helper-text {
          font-size: 0.9rem;
          color: var(--primary-color);
          margin-top: 0.5rem;
          font-weight: 500;
        }
        .accordion-button {
          font-weight: 800;
          font-size: 1.1rem;
          color: #1e293b;
          padding: 0;
          background: none;
          box-shadow: none;
        }
        .accordion-button:not(.collapsed) {
          color: #1e293b;
          background: none;
          box-shadow: none;
        }
        .toggle-switch {
          width: 48px;
          height: 28px;
          background-color: #e2e8f0;
          border-radius: 14px;
          position: relative;
          transition: background-color 0.2s;
          cursor: pointer;
        }
        .toggle-switch.active {
          background-color: var(--primary-color);
        }
        .toggle-handle {
          width: 24px;
          height: 24px;
          background-color: white;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: transform 0.2s;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .toggle-switch.active .toggle-handle {
          transform: translateX(20px);
        }
        .item-card {
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 1rem;
            margin-bottom: 1rem;
        }
        .add-item-btn {
            width: 100%;
            padding: 1rem;
            border: 1px dashed var(--primary-color);
            color: var(--primary-color);
            background: #f5f3ff;
            border-radius: 12px;
            font-weight: 600;
            transition: all 0.2s;
        }
        .add-item-btn:hover {
            background: #ede9fe;
        }
      `}</style>

      <div className="form-header shadow-sm">
        <div className="header-top">
          <Link to="/" state={{ activeTab: 'settings' }} className="back-btn me-3">
            <i className="bi bi-chevron-left"></i>
          </Link>
          <h1 className="page-title">{id ? '점검표 수정' : '점검표 등록'}</h1>
        </div>
        <div className="header-buttons">
          {!id && <button className="action-btn btn-load" onClick={fetchTemplates}>점검표 불러오기</button>}
          <button className="action-btn btn-register" onClick={handleSubmit} disabled={loading}>
            {loading ? '저장 중...' : (id ? '저장' : '등록')}
          </button>
        </div>
      </div>

      <div className="mt-4 pb-5" style={{ maxWidth: '768px' }}>

        {/* 1. Name Section */}
        <div className="form-section shadow-sm">
          <label className="form-label">점검표 이름</label>
          <input
            type="text"
            className="form-control"
            placeholder="예시) 화장실 점검표"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="helper-text">* 점검표 이름은 나중에 언제든 수정할 수 있어요.</div>
        </div>

        {/* 2. Description (Accordion) */}
        <div className="form-section shadow-sm">
          <div className="accordion" id="accordionExample">
            <div className="accordion-item border-0">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">
                  안내 사항 (선택)
                </button>
              </h2>
              <div id="collapseOne" className="accordion-collapse collapse" data-bs-parent="#accordionExample">
                <div className="accordion-body">
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="작업자에게 전달할 안내 사항을 입력하세요."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  ></textarea>
                  <div className="text-muted small mt-2">입력한 내용은 모바일 점검표 상단에 항상 노출되어 작업자가 확인할 수 있습니다.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Inspection Items Section */}
        <div className="form-section shadow-sm">
          <div className="d-flex align-items-center mb-3">
            <label className="form-label mb-0 me-2">점검 항목</label>
            <i className="bi bi-question-circle-fill text-black-50"></i>
          </div>
          <p className="text-muted small mb-4">
            점검할 때 확인할 항목을 입력해 주세요. 최소 1개 이상 등록해야 합니다.
          </p>

          {items.map((item, index) => (
            <div key={index} className={`item-card ${item.type === 'SECTION' ? 'bg-light border-0' : ''}`}>
              {item.type === 'SECTION' ? (
                // Section Item UI
                <div className="p-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="badge bg-dark">섹션</span>
                    <button className="btn btn-link text-muted p-0" onClick={() => handleRemoveItem(index)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-lg fw-bold mb-2 border-0 bg-transparent px-0"
                    placeholder="섹션명"
                    value={item.content}
                    onChange={(e) => handleItemChange(index, 'content', e.target.value)}
                    style={{ fontSize: '1.2rem' }}
                  />
                  <input
                    type="text"
                    className="form-control border-0 bg-transparent px-0 text-muted"
                    placeholder="설명 (선택 사항)"
                    value={item.description || ''}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  />
                </div>
              ) : (
                // Standard Item UI
                <>
                  <div className="d-flex gap-2 mb-3">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CustomDropdown
                        options={[
                          { value: 'check', label: <div className="d-flex align-items-center w-100"><i className="bi bi-check-circle me-2 flex-shrink-0"></i><span className="text-truncate">체크리스트</span></div> },
                          { value: 'check_yn', label: <div className="d-flex align-items-center w-100"><i className="bi bi-check-circle-fill me-2 flex-shrink-0"></i><span className="text-truncate">체크리스트(네/아니오)</span></div> },
                          // 텍스트 입력은 답변 저장 구조가 필요해 아직 현장 앱에서 동작하지 않습니다.
                          { value: 'input', label: <div className="d-flex align-items-center w-100 opacity-50"><i className="bi bi-fonts me-2 flex-shrink-0"></i><span className="text-truncate">텍스트 (준비 중)</span></div> },
                          { value: 'choice', label: <div className="d-flex align-items-center w-100"><i className="bi bi-list-ul me-2 flex-shrink-0"></i><span className="text-truncate">객관식</span></div> },
                          { value: 'checkbox', label: <div className="d-flex align-items-center w-100"><i className="bi bi-check-square me-2 flex-shrink-0"></i><span className="text-truncate">체크박스</span></div> }
                        ]}
                        value={item.type}
                        onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CustomDropdown
                        options={[
                          { value: 'none', label: <div className="d-flex align-items-center w-100"><i className="bi bi-slash-circle me-2 flex-shrink-0"></i><span className="text-truncate">사진 등록 기능 미사용</span></div> },
                          { value: 'optional', label: <div className="d-flex align-items-center w-100"><i className="bi bi-camera me-2 flex-shrink-0"></i><span className="text-truncate">사진 등록 선택</span></div> },
                          { value: 'required', label: <div className="d-flex align-items-center w-100"><i className="bi bi-camera-fill me-2 flex-shrink-0"></i><span className="text-truncate">사진 등록 필수</span></div> }
                        ]}
                        value={item.photoAttachmentOption || 'none'}
                        onChange={(e) => handleItemChange(index, 'photoAttachmentOption', e.target.value)}
                        placeholder="사진 등록 설정"
                      />
                    </div>
                  </div>

                  <input
                    type="text"
                    className="form-control mb-3 p-3 bg-light border-0"
                    placeholder="점검 내용을 입력하세요"
                    value={item.content}
                    onChange={(e) => handleItemChange(index, 'content', e.target.value)}
                    style={{ fontSize: '1.1rem' }}
                  />

                  {/* Options for Choice/Checkbox */}
                  {(item.type === 'choice' || item.type === 'checkbox') && (
                    <div className="mb-3 ps-2">
                      {item.options && item.options.map((option, optIndex) => (
                        <div key={optIndex} className="d-flex align-items-center mb-2">
                          <i className={`bi ${item.type === 'choice' ? 'bi-circle' : 'bi-square'} text-black-50 me-2 fs-5`}></i>
                          <input
                            type="text"
                            className="form-control border-0 border-bottom rounded-0 px-0 me-2"
                            value={option}
                            onChange={(e) => handleOptionChange(index, optIndex, e.target.value)}
                            placeholder={`옵션 ${optIndex + 1}`}
                          />
                          <button
                            className="btn btn-link text-danger p-0"
                            onClick={() => handleRemoveOption(index, optIndex)}
                            style={{ opacity: 0.5 }}
                          >
                            <i className="bi bi-dash-circle-fill fs-5"></i>
                          </button>
                        </div>
                      ))}
                      <button
                        className="btn btn-link text-dark text-decoration-none fw-bold p-0 mt-1"
                        onClick={() => handleAddOption(index)}
                      >
                        <i className="bi bi-plus-lg me-1"></i> 옵션 추가
                      </button>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`required-${index}`}
                        checked={item.isRequired || false}
                        onChange={(e) => handleItemChange(index, 'isRequired', e.target.checked)}
                      />
                      <label className="form-check-label small fw-bold" htmlFor={`required-${index}`}>답변 필수</label>
                    </div>
                    <div className="d-flex gap-2">
                      {/* Per-item add buttons removed as per request */}
                      <button className="btn btn-light btn-sm border" onClick={() => {
                        const newItems = [...items];
                        newItems.splice(index + 1, 0, { ...item });
                        setItems(newItems);
                      }} title="복사">
                        <i className="bi bi-files"></i>
                      </button>
                      <button className="btn btn-light btn-sm border" onClick={() => handleRemoveItem(index)} title="삭제">
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}

          <div className="d-flex justify-content-center gap-3 mt-4">
            <button
              className="btn btn-white border shadow-sm rounded-circle d-flex align-items-center justify-content-center btn-icon-circle"
              style={{ width: '50px', height: '50px' }}
              onClick={() => handleAddItem()}
              title="항목 추가"
            >
              <i className="bi bi-plus-lg fs-4"></i>
            </button>
            <button
              className="btn btn-white border shadow-sm rounded-circle d-flex align-items-center justify-content-center btn-icon-circle"
              style={{ width: '50px', height: '50px' }}
              onClick={() => handleAddSection()}
              title="섹션 추가"
            >
              <i className="bi bi-view-list fs-4"></i>
            </button>
          </div>
        </div>

      </div>

      {/* Reuse ChecklistModal logic inline or component */}
      {isModalOpen && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">점검표 선택</h5>
                <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="list-group">
                  {allTemplates.map(template => (
                    <button
                      key={template.id}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistFormPage;
